import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  UserX,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "../../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const emptyDraft = (patientId) => ({
  plan_id: null,
  patient_id: patientId,
  title: "",
  notes: "",
  items: [],
  active: true,
});

export default function PlanEditor() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [plans, setPlans] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft(patientId));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [patientsRes, plansRes, exercisesRes, progressRes] = await Promise.all([
        axios.get(`${API}/patients`),
        axios.get(`${API}/plans`, { params: { patient_id: patientId } }),
        axios.get(`${API}/exercises`),
        axios.get(`${API}/patients/${patientId}/progress`),
      ]);
      setPatient(patientsRes.data.find((p) => p.user_id === patientId) || null);
      setPlans(plansRes.data);
      setExercises(exercisesRes.data);
      setProgress(progressRes.data);
    } catch {
      toast.error("Daten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setDraft(emptyDraft(patientId));
    setEditorOpen(true);
  };

  const openEdit = (plan) => {
    setDraft({
      plan_id: plan.plan_id,
      patient_id: patientId,
      title: plan.title,
      notes: plan.notes || "",
      items: plan.items.map((i) => ({ ...i })),
      active: plan.active,
    });
    setEditorOpen(true);
  };

  const addItem = () => {
    setDraft((d) => ({
      ...d,
      items: [...d.items, { exercise_id: "", sets: 3, reps: 10, rest_seconds: 60, notes: "" }],
    }));
  };

  const updateItem = (idx, field, value) => {
    setDraft((d) => {
      const items = d.items.map((item, i) => (i === idx ? { ...item, [field]: value } : item));
      return { ...d, items };
    });
  };

  const removeItem = (idx) => {
    setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!draft.title.trim()) {
      toast.error("Bitte einen Titel angeben");
      return;
    }
    if (draft.items.some((i) => !i.exercise_id)) {
      toast.error("Bitte für jede Zeile eine Übung auswählen");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        patient_id: patientId,
        title: draft.title,
        notes: draft.notes,
        items: draft.items.map((i) => ({
          exercise_id: i.exercise_id,
          sets: Number(i.sets) || 1,
          reps: Number(i.reps) || 1,
          rest_seconds: Number(i.rest_seconds) || 0,
          notes: i.notes || "",
        })),
        active: draft.active,
      };
      if (draft.plan_id) {
        await axios.put(`${API}/plans/${draft.plan_id}`, payload);
      } else {
        await axios.post(`${API}/plans`, payload);
      }
      toast.success("Trainingsplan gespeichert");
      setEditorOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removePlan = async (planId) => {
    try {
      await axios.delete(`${API}/plans/${planId}`);
      toast.success("Plan gelöscht");
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const deletePatient = async () => {
    try {
      await axios.delete(`${API}/patients/${patientId}`);
      toast.success("Patient gelöscht");
      navigate("/therapist");
    } catch {
      toast.error("Patient konnte nicht gelöscht werden");
    }
  };

  const activatePlan = async (plan) => {
    try {
      await axios.put(`${API}/plans/${plan.plan_id}`, {
        patient_id: patientId,
        title: plan.title,
        notes: plan.notes || "",
        items: plan.items,
        active: true,
      });
      toast.success("Plan aktiviert");
      load();
    } catch {
      toast.error("Aktivierung fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="plan-editor-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const exerciseTitle = (id) => exercises.find((e) => e.exercise_id === id)?.title || "Übung gelöscht";
  const weekDone = progress?.days?.reduce((sum, d) => sum + d.completed, 0) || 0;
  const weekTotal = progress?.days?.reduce((sum, d) => sum + d.total, 0) || 0;

  return (
    <div className="space-y-8" data-testid="plan-editor-page">
      <div>
        <button
          onClick={() => navigate("/therapist")}
          className="mb-4 flex items-center gap-1 text-sm font-medium text-zinc-400 hover:text-primary"
          data-testid="back-to-patients-link"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zu Patienten
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl" data-testid="plan-editor-patient-name">
              {patient?.name || "Patient"}
            </h1>
            <p className="mt-2 text-base text-zinc-400">{patient?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-red-200 text-destructive hover:bg-red-950/50 hover:text-destructive"
                  data-testid="delete-patient-button"
                >
                  <UserX className="mr-2 h-4 w-4" />
                  Patient löschen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-patient-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Patient wirklich löschen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {patient?.name} und alle zugehörigen Trainingspläne sowie der gesamte
                    Fortschritt werden unwiderruflich gelöscht.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete-patient-button">Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deletePatient}
                    className="bg-destructive text-white hover:bg-destructive/90"
                    data-testid="confirm-delete-patient-button"
                  >
                    Endgültig löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button onClick={openNew} className="bg-primary hover:bg-primary/90" data-testid="new-plan-button">
              <Plus className="mr-2 h-4 w-4" />
              Neuer Trainingsplan
            </Button>
          </div>
        </div>
      </div>

      {progress && progress.total_today > 0 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-5 shadow-sm" data-testid="patient-progress-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-zinc-100">Fortschritt der letzten 7 Tage</p>
            <p className="text-sm text-zinc-400" data-testid="patient-progress-summary">
              {weekDone} von {weekTotal} Übungen · Serie: {progress.streak}{" "}
              {progress.streak === 1 ? "Tag" : "Tage"}
            </p>
          </div>
          <Progress
            value={weekTotal ? Math.round((weekDone / weekTotal) * 100) : 0}
            className="mt-3 h-2"
            data-testid="patient-progress-bar"
          />
        </div>
      )}

      {plans.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-12 text-center" data-testid="no-plans-message">
          <p className="text-zinc-400">
            Noch kein Trainingsplan. Erstelle den ersten Plan für diesen Patienten.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="plans-list">
          {plans.map((plan) => (
            <div
              key={plan.plan_id}
              className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 shadow-sm"
              data-testid={`plan-card-${plan.plan_id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-lg font-semibold text-zinc-100">{plan.title}</h3>
                    {plan.active && (
                      <Badge className="bg-accent text-primary hover:bg-accent" data-testid={`plan-active-badge-${plan.plan_id}`}>
                        Aktiv
                      </Badge>
                    )}
                  </div>
                  {plan.notes && <p className="mt-1 text-sm text-zinc-400">{plan.notes}</p>}
                </div>
                <div className="flex items-center gap-1">
                  {!plan.active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => activatePlan(plan)}
                      data-testid={`activate-plan-button-${plan.plan_id}`}
                    >
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Aktivieren
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(plan)} data-testid={`edit-plan-button-${plan.plan_id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-zinc-500 hover:text-destructive"
                    onClick={() => removePlan(plan.plan_id)}
                    data-testid={`delete-plan-button-${plan.plan_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-4 divide-y divide-zinc-800 rounded-lg border border-zinc-800">
                {plan.items.map((item, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 text-sm" data-testid={`plan-item-${plan.plan_id}-${idx}`}>
                    <span className="font-medium text-zinc-100">{exerciseTitle(item.exercise_id)}</span>
                    <span className="text-zinc-400">
                      {item.sets} Sätze × {item.reps} Wdh.
                    </span>
                    {item.rest_seconds > 0 && (
                      <span className="text-zinc-500">Pause: {item.rest_seconds}s</span>
                    )}
                    {item.notes && <span className="italic text-zinc-500">{item.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="plan-editor-dialog">
          <DialogHeader>
            <DialogTitle>{draft.plan_id ? "Trainingsplan bearbeiten" : "Neuer Trainingsplan"}</DialogTitle>
            <DialogDescription>Übungen mit Sätzen, Wiederholungen und Notizen zusammenstellen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="plan-editor-form">
            <div className="space-y-2">
              <Label htmlFor="plan-title">Titel</Label>
              <Input
                id="plan-title"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder="z. B. Knie-Reha Woche 1–2"
                data-testid="plan-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan-notes">Notizen für den Patienten (optional)</Label>
              <Textarea
                id="plan-notes"
                rows={2}
                value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                placeholder="Allgemeine Hinweise zum Plan…"
                data-testid="plan-notes-input"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Übungen</Label>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="add-exercise-row-button">
                  <Plus className="mr-1 h-4 w-4" /> Übung hinzufügen
                </Button>
              </div>
              {draft.items.length === 0 && (
                <p className="rounded-lg bg-zinc-950 px-4 py-3 text-sm text-zinc-400" data-testid="plan-items-empty">
                  Noch keine Übungen im Plan.
                </p>
              )}
              {draft.items.map((item, idx) => (
                <div
                  key={idx}
                  className="space-y-3 rounded-lg border border-zinc-800 p-4"
                  data-testid={`plan-item-row-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Select
                        value={item.exercise_id}
                        onValueChange={(v) => updateItem(idx, "exercise_id", v)}
                      >
                        <SelectTrigger data-testid={`plan-item-exercise-select-${idx}`}>
                          <SelectValue placeholder="Übung auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {exercises.map((ex) => (
                            <SelectItem key={ex.exercise_id} value={ex.exercise_id} data-testid={`plan-item-exercise-option-${idx}-${ex.exercise_id}`}>
                              {ex.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(idx)}
                      className="text-zinc-500 hover:text-destructive"
                      data-testid={`remove-plan-item-button-${idx}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-zinc-400">Sätze</Label>
                      <Input
                        type="number"
                        min={1}
                        value={item.sets}
                        onChange={(e) => updateItem(idx, "sets", e.target.value)}
                        data-testid={`plan-item-sets-input-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Wiederholungen</Label>
                      <Input
                        type="number"
                        min={1}
                        value={item.reps}
                        onChange={(e) => updateItem(idx, "reps", e.target.value)}
                        data-testid={`plan-item-reps-input-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Pause (Sek.)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={item.rest_seconds}
                        onChange={(e) => updateItem(idx, "rest_seconds", e.target.value)}
                        data-testid={`plan-item-rest-input-${idx}`}
                      />
                    </div>
                  </div>
                  <Input
                    value={item.notes}
                    onChange={(e) => updateItem(idx, "notes", e.target.value)}
                    placeholder="Notiz zur Übung (optional)"
                    data-testid={`plan-item-notes-input-${idx}`}
                  />
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))}
                className="h-4 w-4 rounded border-zinc-700 accent-green-500"
                data-testid="plan-active-checkbox"
              />
              Als aktiven Plan setzen (ersetzt den bisherigen aktiven Plan)
            </label>

            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="plan-save-button"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Plan speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
